import { IPreviewToken } from './docxPreviewDom';

export interface IUnfilledToken {
    /** As written in the template, e.g. grantee or «LandmanPhone». */
    token: string;
    count: number;
    /** A LandHawk token spelled almost the same, when there is one. */
    suggestion?: string;
}

export interface ITemplateTokenCheck {
    total: number;
    filled: number;
    unfilled: IUnfilledToken[];
}

/**
 * Matches template tokens against the merge's token list (from documentTemplate/merge-tokens), which spells
 * numbered families with a '#', e.g. SignatureName#. Case-insensitive, like the merge.
 */
export function mergeTokenMatcher(knownNames: string[]): (token: string) => boolean {
    const exact = new Set<string>();
    const families: RegExp[] = [];
    knownNames.forEach((name) => {
        if (name.indexOf('#') >= 0) {
            families.push(new RegExp(`^${name.replace('#', '\\d+')}$`, 'i'));
        } else {
            exact.add(name.toLowerCase());
        }
    });

    return (token: string) => exact.has(token.toLowerCase()) || families.some((family) => family.test(token));
}

export function checkTemplateTokens(tokens: IPreviewToken[], knownNames: string[]): ITemplateTokenCheck {
    const unfilled = new Map<string, IUnfilledToken>();
    let filled = 0;

    tokens.forEach((t) => {
        if (t.kind === 'known') {
            filled++;
        } else if (t.kind === 'unknown') {
            const key = t.token.toLowerCase();
            const entry = unfilled.get(key) || { token: t.token, count: 0, suggestion: closestToken(t.token, knownNames) };
            entry.count++;
            unfilled.set(key, entry);
        }
    });

    return { total: tokens.length, filled, unfilled: Array.from(unfilled.values()) };
}

/** Catches typos (PrimaryTermUnit for PrimaryTermUnits), not different words that happen to be close. */
function closestToken(token: string, knownNames: string[]): string {
    const target = token.toLowerCase();
    const allowed = target.length > 12 ? 3 : 2;
    let best: string;
    let bestDistance = allowed + 1;

    knownNames
        .filter((name) => name.indexOf('#') < 0)
        .forEach((name) => {
            const distance = editDistance(target, name.toLowerCase());
            if (distance < bestDistance) {
                best = name;
                bestDistance = distance;
            }
        });

    return best;
}

function editDistance(a: string, b: string): number {
    let previous = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
        const current = [i];
        for (let j = 1; j <= b.length; j++) {
            current[j] = Math.min(previous[j] + 1, current[j - 1] + 1, previous[j - 1] + (a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1));
        }
        previous = current;
    }
    return previous[b.length];
}



// WEBPACK FOOTER //
// ./src/components/common/docxPreview/templateTokenCheck.ts